import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Search,
  MoreVertical,
  CheckCircle,
  Calendar,
  User,
  Tag,
  Paperclip,
  MessageSquare,
  Loader2,
  AlertCircle,
  Edit2,
  Trash2,
  GripVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import taskService from '@/services/taskService';
import TaskCardModal from './TaskCardModal';

const TaskKanban = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [board, setBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingCard, setDraggingCard] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [creatingCardInColumn, setCreatingCardInColumn] = useState(null);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [savingCard, setSavingCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  
  // Column management states
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [columnFormData, setColumnFormData] = useState({ title: '', color: '#3b82f6' });
  const [savingColumn, setSavingColumn] = useState(false);
  const [draggingColumn, setDraggingColumn] = useState(null);

  useEffect(() => {
    loadBoardData();
  }, [boardId]);

  const loadBoardData = async () => {
    setLoading(true);
    try {
      const [boardData, cardsData] = await Promise.all([
        taskService.getBoardById(boardId),
        taskService.getBoardCards(boardId)
      ]);

      setBoard(boardData);
      setColumns(boardData.columns || []);
      setCards(cardsData || []);
    } catch (error) {
      toast({
        title: 'Erro ao carregar board',
        description: 'Não foi possível carregar os dados do board.',
        variant: 'destructive',
      });
      navigate('/portal/tasks');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredCards = (columnId) => {
    let filtered = cards.filter(card => card.column_id === columnId);

    if (searchTerm) {
      filtered = filtered.filter(card =>
        card.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterPriority && filterPriority !== "all") {
      filtered = filtered.filter(card => card.priority === filterPriority);
    }

    if (!showCompleted) {
      filtered = filtered.filter(card => !card.is_completed);
    }

    return filtered.sort((a, b) => a.order - b.order);
  };

  const handleDragStart = (e, card) => {
    console.log('🚀 DragStart - Card:', card);
    setDraggingCard(card);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragEnd = () => {
    console.log('🏁 DragEnd');
    setDraggingCard(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, targetColumnId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverColumn(null);

    console.log('🎯 Drop detectado!');
    console.log('Card sendo arrastado:', draggingCard);
    console.log('Coluna alvo:', targetColumnId);

    if (!draggingCard) {
      console.log('⚠️ Nenhum card sendo arrastado');
      return;
    }

    if (draggingCard.column_id === targetColumnId) {
      console.log('⚠️ Card já está na mesma coluna');
      setDraggingCard(null);
      return;
    }

    const cardToMove = draggingCard;
    
    try {
      const cardsInTargetColumn = cards.filter(c => c.column_id === targetColumnId);
      const newOrder = cardsInTargetColumn.length;

      console.log('🔄 Movendo card...', { cardId: cardToMove._id, targetColumnId, newOrder });

      await taskService.moveCard(cardToMove._id, targetColumnId, newOrder);

      console.log('✅ Card movido com sucesso!');

      setCards(cards.map(card =>
        card._id === cardToMove._id
          ? { ...card, column_id: targetColumnId, order: newOrder }
          : card
      ));

      const newColumn = columns.find(c => c._id === targetColumnId);
      toast({
        title: 'Card movido!',
        description: `"${cardToMove.title}" foi movido para ${newColumn?.title}`,
      });
    } catch (error) {
      console.error('❌ Erro ao mover card:', error);
      console.error('Detalhes do erro:', error.response?.data);
      
      toast({
        title: 'Erro ao mover card',
        description: error.response?.data?.detail || 'Não foi possível mover o card. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setDraggingCard(null);
    }
  };

  const handleQuickCreateCard = async (columnId) => {
    if (!newCardTitle.trim()) {
      toast({
        title: 'Título obrigatório',
        description: 'Digite um título para o card.',
        variant: 'destructive',
      });
      return;
    }

    setSavingCard(true);
    try {
      const cardData = {
        board_id: boardId,
        column_id: columnId,
        title: newCardTitle,
        priority: 'media'
      };

      const response = await taskService.createCard(cardData);
      setCards([...cards, response.card]);
      setNewCardTitle('');
      setCreatingCardInColumn(null);

      toast({
        title: 'Sucesso!',
        description: 'Card criado com sucesso.',
      });
    } catch (error) {
      toast({
        title: 'Erro ao criar card',
        description: 'Não foi possível criar o card.',
        variant: 'destructive',
      });
    } finally {
      setSavingCard(false);
    }
  };

  const handleOpenCard = (cardId) => {
    console.log('🟢 TaskKanban: Abrindo card:', cardId);
    const card = cards.find(c => c._id === cardId);
    console.log('🔍 TaskKanban: Card encontrado no estado:', {
      _id: card?._id,
      title: card?.title,
      project_id: card?.project_id,
      lead_id: card?.lead_id,
      ticket_id: card?.ticket_id
    });
    if (card) {
      setSelectedCard(card);
      setShowCardModal(true);
      console.log('✅ TaskKanban: Modal configurado para abrir');
    } else {
      console.error('❌ TaskKanban: Card não encontrado com id:', cardId);
    }
  };

  const handleCardUpdated = async (updatedCard) => {
    // Update only the specific card in state, don't reload entire board
    if (updatedCard) {
      console.log('🔄 TaskKanban: Atualizando card no estado pai:', updatedCard._id);
      setCards(prevCards => {
        const newCards = prevCards.map(c => c._id === updatedCard._id ? updatedCard : c);
        console.log('✅ TaskKanban: Cards atualizados');
        return newCards;
      });
    }
    // If no updatedCard provided, do a minimal reload
    else {
      console.log('⚠️ TaskKanban: Nenhum card fornecido, recarregando dados...');
      await loadBoardData();
    }
  };

  // Column Management Functions
  const handleOpenColumnModal = (column = null) => {
    if (column) {
      setEditingColumn(column);
      setColumnFormData({ title: column.title, color: column.color || '#3b82f6' });
    } else {
      setEditingColumn(null);
      setColumnFormData({ title: '', color: '#3b82f6' });
    }
    setShowColumnModal(true);
  };

  const handleCloseColumnModal = () => {
    setShowColumnModal(false);
    setEditingColumn(null);
    setColumnFormData({ title: '', color: '#3b82f6' });
  };

  const handleSaveColumn = async () => {
    if (!columnFormData.title.trim()) {
      toast({
        title: 'Título obrigatório',
        description: 'Digite um título para a coluna.',
        variant: 'destructive',
      });
      return;
    }

    setSavingColumn(true);
    try {
      if (editingColumn) {
        // Update existing column
        await taskService.updateColumn(editingColumn._id, columnFormData);
        setColumns(columns.map(col => 
          col._id === editingColumn._id ? { ...col, ...columnFormData } : col
        ));
        toast({
          title: 'Sucesso!',
          description: 'Coluna atualizada com sucesso.',
        });
      } else {
        // Create new column
        const newColumn = await taskService.createColumn({
          board_id: boardId,
          ...columnFormData,
          order: columns.length
        });
        setColumns([...columns, newColumn]);
        toast({
          title: 'Sucesso!',
          description: 'Coluna criada com sucesso.',
        });
      }
      handleCloseColumnModal();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível salvar a coluna.',
        variant: 'destructive',
      });
    } finally {
      setSavingColumn(false);
    }
  };

  const handleDeleteColumn = async (columnId) => {
    const cardsInColumn = cards.filter(card => card.column_id === columnId);
    
    if (cardsInColumn.length > 0) {
      toast({
        title: 'Não é possível deletar',
        description: `Esta coluna possui ${cardsInColumn.length} card(s). Mova ou delete os cards primeiro.`,
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm('Tem certeza que deseja deletar esta coluna?')) {
      return;
    }

    try {
      await taskService.deleteColumn(columnId);
      setColumns(columns.filter(col => col._id !== columnId));
      toast({
        title: 'Sucesso!',
        description: 'Coluna deletada com sucesso.',
      });
    } catch (error) {
      toast({
        title: 'Erro ao deletar',
        description: error.response?.data?.detail || 'Não foi possível deletar a coluna.',
        variant: 'destructive',
      });
    }
  };

  // Column Drag and Drop for reordering
  const handleColumnDragStart = (e, column) => {
    setDraggingColumn(column);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = async (e, targetColumn) => {
    e.preventDefault();
    
    if (!draggingColumn || draggingColumn._id === targetColumn._id) {
      setDraggingColumn(null);
      return;
    }

    try {
      const oldIndex = columns.findIndex(col => col._id === draggingColumn._id);
      const newIndex = columns.findIndex(col => col._id === targetColumn._id);
      
      const reorderedColumns = [...columns];
      const [removed] = reorderedColumns.splice(oldIndex, 1);
      reorderedColumns.splice(newIndex, 0, removed);
      
      // Update order values
      const updatedColumns = reorderedColumns.map((col, index) => ({
        ...col,
        order: index
      }));
      
      setColumns(updatedColumns);
      
      // Update in backend - send all column orders
      const columnOrders = updatedColumns.map(col => ({
        column_id: col._id,
        order: col.order
      }));
      
      await taskService.reorderColumns(columnOrders);
      
      toast({
        title: 'Coluna reordenada!',
        description: `"${draggingColumn.title}" foi reordenada com sucesso.`,
      });
    } catch (error) {
      console.error('Erro ao reordenar coluna:', error);
      // Revert on error
      loadBoardData();
      toast({
        title: 'Erro ao reordenar',
        description: 'Não foi possível reordenar a coluna.',
        variant: 'destructive',
      });
    } finally {
      setDraggingColumn(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Carregando board...</p>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600">Board não encontrado</p>
          <Button onClick={() => navigate('/portal/tasks')} className="mt-4">
            Voltar para Boards
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal/tasks')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>

            <div className="flex items-center gap-3">
              <div className="text-3xl" style={{ color: board.background_color }}>
                {board.icon || '📋'}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{board.title}</h1>
                {board.description && (
                  <p className="text-sm text-gray-600">{board.description}</p>
                )}
              </div>
            </div>

            <Badge variant="secondary">
              {taskService.translateVisibility(board.visibility)}
            </Badge>
          </div>
        </div>

        {/* Filtros */}
        <div className="mt-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar cards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todas as prioridades" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as prioridades</SelectItem>
              <SelectItem value="baixa">Baixa</SelectItem>
              <SelectItem value="media">Média</SelectItem>
              <SelectItem value="alta">Alta</SelectItem>
              <SelectItem value="urgente">Urgente</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant={showCompleted ? "default" : "outline"}
            size="sm"
            onClick={() => setShowCompleted(!showCompleted)}
            className="gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            {showCompleted ? 'Ocultar Concluídos' : 'Mostrar Concluídos'}
          </Button>

          {(searchTerm || (filterPriority && filterPriority !== "all")) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setFilterPriority('all');
              }}
            >
              Limpar Filtros
            </Button>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
        <div className="flex gap-4 h-full" style={{ minWidth: 'min-content' }}>
          {columns.map((column) => {
            const columnCards = getFilteredCards(column._id);
            const isOver = dragOverColumn === column._id;

            return (
              <div
                key={column._id}
                className="flex-shrink-0 w-80 flex flex-col"
              >
                {/* Column Header */}
                <div
                  draggable
                  onDragStart={(e) => handleColumnDragStart(e, column)}
                  onDragOver={handleColumnDragOver}
                  onDrop={(e) => handleColumnDrop(e, column)}
                  className={`
                    bg-white rounded-t-lg border-b-4 p-3 flex-shrink-0
                    ${draggingColumn?._id === column._id ? 'opacity-50' : ''}
                  `}
                  style={{ borderBottomColor: column.color || '#gray' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1">
                      <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        {column.title}
                        <Badge variant="secondary" className="text-xs">
                          {columnCards.length}
                        </Badge>
                      </h3>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => {
                          setTimeout(() => setCreatingCardInColumn(column._id), 0);
                        }}>
                          <Plus className="h-4 w-4 mr-2" />
                          Adicionar Card
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => {
                          setTimeout(() => handleOpenColumnModal(column), 0);
                        }}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Editar Coluna
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onSelect={() => {
                            setTimeout(() => handleDeleteColumn(column._id), 0);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Deletar Coluna
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {creatingCardInColumn !== column._id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-gray-600 hover:text-gray-900"
                      onClick={() => setCreatingCardInColumn(column._id)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar card
                    </Button>
                  )}
                </div>

                {/* Column Content */}
                <div 
                  className={`bg-gray-100 flex-1 overflow-y-auto p-3 space-y-3 rounded-b-lg ${isOver ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                  onDragOver={(e) => handleDragOver(e, column._id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column._id)}
                >
                  {creatingCardInColumn === column._id && (
                    <Card className="border-2 border-blue-500">
                      <CardContent className="p-3 space-y-2">
                        <Input
                          placeholder="Título do card..."
                          value={newCardTitle}
                          onChange={(e) => setNewCardTitle(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleQuickCreateCard(column._id);
                            }
                          }}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleQuickCreateCard(column._id)}
                            disabled={savingCard}
                            className="flex-1"
                          >
                            {savingCard ? 'Criando...' : 'Adicionar'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setCreatingCardInColumn(null);
                              setNewCardTitle('');
                            }}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {columnCards.map((card) => (
                    <Card
                      key={card._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, card)}
                      onDragEnd={handleDragEnd}
                      className={`
                        cursor-pointer hover:shadow-lg transition-all relative
                        ${draggingCard?._id === card._id ? 'opacity-50 rotate-2' : ''}
                        ${card.is_completed ? 'opacity-60' : ''}
                      `}
                    >
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            {card.priority && (
                              <Badge className={taskService.getPriorityColor(card.priority)}>
                                {taskService.getPriorityIcon(card.priority)} {taskService.translatePriority(card.priority)}
                              </Badge>
                            )}
                            {card.is_completed && (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Concluído
                              </Badge>
                            )}
                          </div>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-gray-100">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem 
                                onSelect={() => {
                                  setTimeout(() => handleOpenCard(card._id), 0);
                                }}
                              >
                                <Edit2 className="h-4 w-4 mr-2" />
                                Editar Card
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onSelect={() => {
                                  setTimeout(async () => {
                                    if (window.confirm('Tem certeza que deseja deletar este card?')) {
                                      try {
                                        await taskService.deleteCard(card._id);
                                        setCards(cards.filter(c => c._id !== card._id));
                                        toast({
                                          title: 'Sucesso!',
                                          description: 'Card deletado com sucesso.',
                                        });
                                      } catch (error) {
                                        toast({
                                          title: 'Erro ao deletar',
                                          description: 'Não foi possível deletar o card.',
                                          variant: 'destructive',
                                        });
                                      }
                                    }
                                  }, 0);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Deletar Card
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <h4 
                          className="font-medium text-gray-900 text-sm leading-snug"
                          onClick={() => {
                            if (!draggingCard) {
                              handleOpenCard(card._id);
                            }
                          }}
                        >
                          {card.title}
                        </h4>

                        {card.labels && card.labels.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {card.labels.map((label, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                <Tag className="h-3 w-3 mr-1" />
                                {label}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {card.due_date && (
                          <div
                            className={`
                              flex items-center gap-1 text-xs
                              ${taskService.isOverdue(card.due_date, card.is_completed)
                                ? 'text-red-600 font-medium'
                                : 'text-gray-600'
                              }
                            `}
                          >
                            <Calendar className="h-3 w-3" />
                            {taskService.formatDueDate(card.due_date)}
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-2 border-t">
                          {card.assigned_to_name && (
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <User className="h-3 w-3" />
                              <span className="truncate max-w-[120px]">
                                {card.assigned_to_name}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-xs text-gray-600 ml-auto">
                            {card.checklist_progress && card.checklist_progress.total > 0 && (
                              <div className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                {card.checklist_progress.completed}/{card.checklist_progress.total}
                              </div>
                            )}

                            {card.comment_count > 0 && (
                              <div className="flex items-center gap-1">
                                <MessageSquare className="h-3 w-3" />
                                {card.comment_count}
                              </div>
                            )}

                            {card.attachment_count > 0 && (
                              <div className="flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                {card.attachment_count}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {columnCards.length === 0 && creatingCardInColumn !== column._id && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      {searchTerm || filterPriority
                        ? 'Nenhum card encontrado'
                        : 'Nenhum card nesta coluna'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Adicionar nova coluna */}
          <div className="flex-shrink-0 w-80">
            <Button
              variant="outline"
              className="w-full h-full min-h-[200px] border-dashed border-2"
              onClick={() => handleOpenColumnModal()}
            >
              <Plus className="h-5 w-5 mr-2" />
              Adicionar Coluna
            </Button>
          </div>
        </div>
      </div>

      {/* Column Modal */}
      <Dialog open={showColumnModal} onOpenChange={handleCloseColumnModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingColumn ? 'Editar Coluna' : 'Nova Coluna'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="column-title">Título da Coluna *</Label>
              <Input
                id="column-title"
                value={columnFormData.title}
                onChange={(e) => setColumnFormData({ ...columnFormData, title: e.target.value })}
                placeholder="Ex: Em Desenvolvimento"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="column-color">Cor da Borda</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="column-color"
                  value={columnFormData.color}
                  onChange={(e) => setColumnFormData({ ...columnFormData, color: e.target.value })}
                  className="h-10 w-20 rounded border cursor-pointer"
                />
                <Input
                  value={columnFormData.color}
                  onChange={(e) => setColumnFormData({ ...columnFormData, color: e.target.value })}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseColumnModal}>
              Cancelar
            </Button>
            <Button onClick={handleSaveColumn} disabled={savingColumn}>
              {savingColumn ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                editingColumn ? 'Atualizar' : 'Criar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Modal */}
      <TaskCardModal
        isOpen={showCardModal && selectedCard !== null}
        onClose={() => {
          setShowCardModal(false);
          setSelectedCard(null);
        }}
        card={selectedCard}
        boardId={boardId}
        onCardUpdated={handleCardUpdated}
      />
    </div>
  );
};

export default TaskKanban;
